// src/components/PreviousRecordings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Mic, User, Clock, Loader2 } from 'lucide-react';
import './PreviousRecordings.css';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

interface Recording {
  id: number;
  nurse_name: string;
  patient_name: string;
  mrn: string;
  original_language: string;
  timestamp: string;
  recording_type: string;
  transcript: string;
  summary: string;
}

interface PreviousRecordingsProps {
  patientMRN: string;
  currentNurseEmail: string;
}

// Recording type display names and icons
const RECORDING_TYPE_INFO: Record<string, { label: string; color: string }> = {
  'Clinical Interaction': { label: 'Clinical Interaction', color: '#3b82f6' },
  'Family Conference': { label: 'Family Conference', color: '#8b5cf6' },
  'Care Plan Discussion': { label: 'Care Plan Discussion', color: '#10b981' },
  'Education Session': { label: 'Education Session', color: '#f59e0b' }
};

// Format timestamp for display
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper function to parse summary into sections
const parseSummary = (summary: string): { mainSummary: string; keyPoints: string[]; breakdown: Record<string, string> } => {
  const result = {
    mainSummary: '',
    keyPoints: [] as string[],
    breakdown: {} as Record<string, string>
  };

  // Split by common section markers
  const lines = summary.split('\n');
  let currentSection = 'summary';
  let currentCategory = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Check for Key Points section
    if (trimmed.toLowerCase().includes('key points') || trimmed.match(/^\d+\./)) {
      if (trimmed.toLowerCase().includes('key points')) {
        currentSection = 'keyPoints';
        continue;
      }
      if (currentSection === 'keyPoints') {
        // Extract the point text (remove numbering)
        const pointText = trimmed.replace(/^\d+\.\s*/, '');
        if (pointText) result.keyPoints.push(pointText);
        continue;
      }
    }

    // Check for categorical breakdown (lines starting with emoji)
    // Common emojis used in categorical breakdowns
    const commonEmojis = ['🫀', '🫁', '🧠', '💧', '🔬', '💊', '⚠️', '🚨', '⏰', '🔍', '👨‍⚕️', '🏥', '😴', '📊', '🩹', '🤢', '🍽️', '🚶‍♂️', '👶', '🍼', '💉', '👪', '🦾', '🏋️', '🩺', '🛁', '🛡️', '🎯', '📋', '🤰', '🩸', '🧪', '🧸', '❤️', '🚽', '👥', '🪥', '👔', '🍴', '🌿', '🗣️', '🧘', '💭', '🛏️', '🧩'];
    const startsWithEmoji = commonEmojis.some(emoji => trimmed.startsWith(emoji));
    const emojiMatch = startsWithEmoji;
    if (emojiMatch) {
      currentSection = 'breakdown';
      // Extract category name and content
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex > 0) {
        currentCategory = trimmed.slice(0, colonIndex).trim();
        result.breakdown[currentCategory] = trimmed.slice(colonIndex + 1).trim();
      } else {
        currentCategory = trimmed;
        result.breakdown[currentCategory] = '';
      }
      continue;
    }

    // Add content to appropriate section
    if (currentSection === 'summary') {
      result.mainSummary += (result.mainSummary ? ' ' : '') + trimmed;
    } else if (currentSection === 'breakdown' && currentCategory) {
      result.breakdown[currentCategory] += (result.breakdown[currentCategory] ? ' ' : '') + trimmed;
    }
  }

  // If no structured content found, use entire summary as main summary
  if (!result.mainSummary && !result.keyPoints.length && !Object.keys(result.breakdown).length) {
    result.mainSummary = summary;
  }

  return result;
};

// Individual recording card component
const RecordingCard: React.FC<{
  recording: Recording;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ recording, isExpanded, onToggle }) => {
  const { theme } = useTheme();
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [keyPointsExpanded, setKeyPointsExpanded] = useState(false);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);

  const typeInfo = RECORDING_TYPE_INFO[recording.recording_type] || { 
    label: recording.recording_type, 
    color: '#6b7280' 
  };

  const parsedContent = parseSummary(recording.summary);

  return (
    <div className={`previous-recording-card ${isExpanded ? 'expanded' : ''}`}>
      <button 
        className="previous-recording-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
        style={{ borderLeftColor: typeInfo.color }}
      >
        <div className="recording-header-left">
          <Mic size={18} style={{ color: typeInfo.color }} />
          <div className="recording-header-info">
            <span className="recording-header-title" style={{ color: typeInfo.color }}>
              {typeInfo.label}
            </span>
            <span className="recording-header-meta">
              From {recording.nurse_name}
            </span>
          </div>
        </div>
        <div className="recording-header-right">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      
      {isExpanded && (
        <div className="previous-recording-content">
          <div className="recording-meta-row">
            <div className="recording-meta-item">
              <User size={14} />
              <span>{recording.nurse_name}</span>
            </div>
            <div className="recording-meta-item">
              <Clock size={14} />
              <span>{formatTimestamp(recording.timestamp)}</span>
            </div>
          </div>

          {/* Summary Section - Collapsible */}
          {parsedContent.mainSummary && (
            <div className="recording-section">
              <button 
                className="recording-section-header"
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <span>Summary</span>
                {summaryExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {summaryExpanded && (
                <div className="recording-section-content">
                  <p>{parsedContent.mainSummary}</p>
                </div>
              )}
            </div>
          )}

          {/* Key Points Section - Collapsible */}
          {parsedContent.keyPoints.length > 0 && (
            <div className="recording-section">
              <button 
                className="recording-section-header"
                onClick={() => setKeyPointsExpanded(!keyPointsExpanded)}
              >
                <span>Key Points ({parsedContent.keyPoints.length})</span>
                {keyPointsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {keyPointsExpanded && (
                <div className="recording-section-content">
                  <ol className="key-points-list">
                    {parsedContent.keyPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* Categorical Breakdown Section - Collapsible */}
          {Object.keys(parsedContent.breakdown).length > 0 && (
            <div className="recording-section">
              <button 
                className="recording-section-header"
                onClick={() => setBreakdownExpanded(!breakdownExpanded)}
              >
                <span>Categorical Breakdown ({Object.keys(parsedContent.breakdown).length})</span>
                {breakdownExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {breakdownExpanded && (
                <div className="recording-section-content breakdown-content">
                  {Object.entries(parsedContent.breakdown).map(([category, content], index) => (
                    <div key={index} className="breakdown-category">
                      <h4>{category}</h4>
                      <p>{content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main component
const PreviousRecordings: React.FC<PreviousRecordingsProps> = ({
  patientMRN,
  currentNurseEmail
}) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRecordings, setExpandedRecordings] = useState<Set<number>>(new Set());
  const [containerExpanded, setContainerExpanded] = useState(false);

  // Fetch previous recordings
  const fetchRecordings = useCallback(async () => {
    if (!patientMRN) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get<{ success: boolean; recordings: Recording[]; message?: string }>(
        `${BACKEND_URL}/other_recordings/previous/${patientMRN}`,
        { params: { exclude_nurse_email: currentNurseEmail } }
      );

      if (response.data.success) {
        setRecordings(response.data.recordings);
      } else {
        setError(response.data.message || 'Failed to fetch recordings');
      }
    } catch (err) {
      console.error('Error fetching previous recordings:', err);
      setError('Failed to load previous recordings');
    } finally {
      setIsLoading(false);
    }
  }, [patientMRN, currentNurseEmail]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const toggleRecording = (id: number) => {
    setExpandedRecordings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Show "no previous recordings" state when no recordings found
  if (!isLoading && recordings.length === 0) {
    return (
      <div className="previous-recordings-container no-recordings">
        <div className="no-recordings-content">
          <Mic size={20} className="recordings-icon" />
          <span className="no-recordings-text">No previous clinical interactions for this patient</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`previous-recordings-container ${containerExpanded ? 'expanded' : ''}`}>
      <button 
        className="previous-recordings-header"
        onClick={() => setContainerExpanded(!containerExpanded)}
        aria-expanded={containerExpanded}
      >
        <div className="recordings-header-left">
          <Mic size={20} className="recordings-icon" />
          <div className="recordings-header-info">
            <span className="recordings-header-title">Previous Clinical Interactions</span>
            <span className="recordings-header-count">
              {isLoading ? 'Loading...' : `${recordings.length} recording type${recordings.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
        <div className="recordings-header-right">
          {isLoading && <Loader2 size={16} className="spin" style={{ marginRight: '8px' }} />}
          {containerExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {containerExpanded && (
        <div className="previous-recordings-content">
          {isLoading ? (
            <div className="recordings-loading">
              <Loader2 size={24} className="spin" />
              <span>Loading previous recordings...</span>
            </div>
          ) : error ? (
            <div className="recordings-error">
              <span>{error}</span>
            </div>
          ) : (
            <div className="recordings-list">
              {recordings.map(recording => (
                <RecordingCard
                  key={recording.id}
                  recording={recording}
                  isExpanded={expandedRecordings.has(recording.id)}
                  onToggle={() => toggleRecording(recording.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreviousRecordings;
